import { AddressAutocompleteInput } from '../../components/common/AddressAutocompleteInput';
import React, { useState } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  Grid,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Skeleton,
  Stack,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/joy';
import {
  ArrowLeft as BackIcon,
  ArrowSquareOut as ExternalLinkIcon,
  Share as ShareIcon,
  UserPlus as CreateLeadIcon,
} from '@phosphor-icons/react';
import { useStormAlert } from '../../hooks/useStormTracker';
import { SeverityBadge } from '../../components/stormtracker/SeverityBadge';
import { OpportunityScoreBadge } from '../../components/stormtracker/OpportunityScoreBadge';
import { AlertMap } from '../../components/stormtracker/AlertMap';
import { EVENT_TYPE_LABELS } from '../../types/stormtracker';
import { apiClient } from '../../lib/api';
import { useNotification } from '../../contexts/NotificationContext';

interface LeadFormState {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  address_1: string;
  city?: string;
  state?: string;
  zip_code?: string;
  latitude?: number;
  longitude?: number;
}

const EMPTY_LEAD: LeadFormState = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  address_1: '',
};

const getEventIcon = (eventType: string): string => {
  switch (eventType) {
    case 'hail':
      return '🌨️';
    case 'wind':
      return '💨';
    case 'tornado':
      return '🌪️';
    default:
      return '⛈️';
  }
};

const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleString([], {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatTimeAgo = (dateString: string): string => {
  const now = new Date();
  const date = new Date(dateString);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 60) return `${diffMins} minutes ago`;
  if (diffHours < 24) return `${diffHours} hours ago`;
  if (diffDays === 1) return 'Yesterday';
  return `${diffDays} days ago`;
};

const AlertDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: alert, isLoading, error } = useStormAlert(id ? parseInt(id) : null);
  const { showError, showSuccess } = useNotification();

  const [leadOpen, setLeadOpen] = useState(false);
  const [lead, setLead] = useState<LeadFormState>(EMPTY_LEAD);
  const [savingLead, setSavingLead] = useState(false);

  // The alert already knows where the storm hit, so the modal only asks for
  // the homeowner. City / county / state / ZIP / coordinates are taken from
  // the alert server-side.
  const handleCreateLead = async () => {
    if (!alert) return;
    if (!lead.first_name.trim() && !lead.last_name.trim()) {
      showError('Enter a first or last name for the homeowner.');
      return;
    }
    setSavingLead(true);
    try {
      const response = await apiClient.post<{ contact: number; opportunity: number | null }>(
        `/api/storm-alerts/${alert.id}/create-lead/`,
        lead,
      );
      showSuccess('Lead created from this alert');
      setLeadOpen(false);
      setLead(EMPTY_LEAD);
      const { contact, opportunity } = response.data;
      navigate(opportunity ? `/crm/opportunities/${opportunity}` : `/crm/contacts/${contact}`);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to create the lead');
    } finally {
      setSavingLead(false);
    }
  };

  const handleShare = () => {
    if (alert && navigator.share) {
      navigator.share({
        title: `Storm Alert: ${EVENT_TYPE_LABELS[alert.event_type]} in ${alert.city || 'Unknown'}`,
        text: `${alert.severity.toUpperCase()} ${alert.event_type} alert with opportunity score of ${alert.opportunity_score}`,
        url: window.location.href,
      });
    } else if (alert) {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  const openInGoogleMaps = () => {
    if (alert) {
      const url = `https://www.google.com/maps/search/?api=1&query=${alert.latitude},${alert.longitude}`;
      window.open(url, '_blank');
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ minWidth: 0 }}>
        <Skeleton variant="text" width={200} height={40} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={300} sx={{ mb: 2, borderRadius: 'md' }} />
        <Grid container spacing={2}>
          {[1, 2, 3, 4].map((i) => (
            <Grid key={i} xs={6} md={3}>
              <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 'md' }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (error || !alert) {
    return (
      <Box sx={{ minWidth: 0 }}>
        <Alert color="danger">Alert not found or failed to load.</Alert>
        <Button
          variant="outlined"
          startDecorator={<BackIcon />}
          onClick={() => navigate('/advanced/storm-tracker/alerts')}
          sx={{ mt: 2 }}
        >
          Back to Alerts
        </Button>
      </Box>
    );
  }

  const magnitude = alert.magnitude_display ? {
    value: alert.magnitude_display,
    label: { hail: 'Hail size', wind: 'Wind speed', tornado: 'Rating' }[alert.event_type],
  } : null;
  const location = [alert.city, alert.county, alert.state].filter(Boolean).join(', ') || 'Unknown location';

  return (
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <IconButton variant="outlined" color="neutral" onClick={() => navigate(-1)}>
          <BackIcon />
        </IconButton>
        <Typography level="body-md" sx={{ color: 'text.secondary' }}>
          <RouterLink to="/advanced/storm-tracker/alerts" style={{ color: 'inherit', textDecoration: 'none' }}>
            Alerts
          </RouterLink>
          {' / '}Alert Details
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography sx={{ fontSize: '2.5rem' }}>{getEventIcon(alert.event_type)}</Typography>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <SeverityBadge severity={alert.severity} size="md" />
              <Chip variant="outlined" size="md">
                {EVENT_TYPE_LABELS[alert.event_type]}
              </Chip>
            </Box>
            <Typography level="h2">{location}</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Share Alert">
            <IconButton variant="outlined" color="neutral" onClick={handleShare}>
              <ShareIcon />
            </IconButton>
          </Tooltip>
          <Button variant="outlined" color="primary" endDecorator={<ExternalLinkIcon />} onClick={openInGoogleMaps}>
            View on Maps
          </Button>
          <Button color="primary" startDecorator={<CreateLeadIcon />} onClick={() => setLeadOpen(true)}>
            Create Lead
          </Button>
        </Box>
      </Box>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <AlertMap alert={alert} height={350} />
      </Card>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid xs={6} sm={3}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <SeverityBadge severity={alert.severity} size="lg" />
              <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 1 }}>
                Severity
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid xs={6} sm={3}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <OpportunityScoreBadge score={alert.opportunity_score} size="lg" />
              <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 1 }}>
                Opportunity Score
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        {magnitude && (
          <Grid xs={6} sm={3}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography level="h3" sx={{ color: 'var(--brand-text, #7013f5)' }}>
                  {magnitude.value}
                </Typography>
                <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 1 }}>
                  {magnitude.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
        <Grid xs={6} sm={3}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography level="title-lg">{formatTimeAgo(alert.event_timestamp)}</Typography>
              <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 1 }}>
                Time
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card variant="outlined">
        <CardContent>
          <Typography level="title-md" sx={{ mb: 2 }}>
            Alert Details
          </Typography>
          <Grid container spacing={2}>
            <Grid xs={12} sm={6}>
              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                Location
              </Typography>
              <Typography level="body-md">
                {location}
                {alert.zip_code && ` (${alert.zip_code})`}
              </Typography>
            </Grid>
            <Grid xs={12} sm={6}>
              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                Coordinates
              </Typography>
              <Typography level="body-md">
                {alert.latitude.toFixed(4)}, {alert.longitude.toFixed(4)}
              </Typography>
            </Grid>
            {alert.territory_name && (
              <Grid xs={12} sm={6}>
                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                  Territory
                </Typography>
                <Chip
                  size="sm"
                  variant="soft"
                  color="primary"
                  component={RouterLink}
                  to={`/advanced/storm-tracker/territories/${alert.territory}`}
                  sx={{ cursor: 'pointer' }}
                >
                  {alert.territory_name}
                </Chip>
              </Grid>
            )}
            <Grid xs={12} sm={6}>
              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                Data Source
              </Typography>
              <Typography level="body-md">{alert.data_source}</Typography>
            </Grid>
            <Grid xs={12} sm={6}>
              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                Event Time
              </Typography>
              <Typography level="body-md">{formatDateTime(alert.event_timestamp)}</Typography>
            </Grid>
            <Grid xs={12} sm={6}>
              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                Recorded
              </Typography>
              <Typography level="body-md">{formatDateTime(alert.created_at)}</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Modal open={leadOpen} onClose={() => setLeadOpen(false)}>
        <ModalDialog sx={{ minWidth: 380 }}>
          <ModalClose />
          <DialogTitle>Create Lead</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert color="neutral" variant="soft">
                Default location (select an address to replace it): {location}
                {alert.zip_code && ` ${alert.zip_code}`}
              </Alert>
              <Stack direction="row" spacing={1}>
                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>First Name</FormLabel>
                  <Input
                    value={lead.first_name}
                    onChange={(e) => setLead({ ...lead, first_name: e.target.value })}
                  />
                </FormControl>
                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>Last Name</FormLabel>
                  <Input
                    value={lead.last_name}
                    onChange={(e) => setLead({ ...lead, last_name: e.target.value })}
                  />
                </FormControl>
              </Stack>
              <FormControl>
                <FormLabel>Street Address</FormLabel>
                <AddressAutocompleteInput
                  value={lead.address_1}
                  onChange={address => setLead(previous => ({ ...previous, address_1: address, city: undefined, state: undefined, zip_code: undefined, latitude: undefined, longitude: undefined }))}
                  onAddressSelect={parsed => setLead(previous => ({ ...previous, address_1: parsed.address_1, city: parsed.city, state: parsed.state, zip_code: parsed.zip_code, latitude: parsed.lat, longitude: parsed.lng }))}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  value={lead.email}
                  onChange={(e) => setLead({ ...lead, email: e.target.value })}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Phone</FormLabel>
                <Input value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} />
              </FormControl>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button variant="plain" color="neutral" onClick={() => setLeadOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreateLead} loading={savingLead}>
                  Create Lead
                </Button>
              </Stack>
            </Stack>
          </DialogContent>
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default AlertDetail;
